/**
 * readThrough — serve a read local-first, resilient to flaky connectivity.
 *
 * `navigator.onLine` is unreliable (it can report "online" while every request
 * fails with ERR_INTERNET_DISCONNECTED, and stays "true" when only the server
 * is down). So we do NOT gate on it: we attempt the network and fall back to
 * the cached response on ANY failure. When the browser is confidently offline
 * AND we already have a cached copy, we skip the doomed request for speed.
 *
 * Online success refreshes the cache (write-through). Used for read-only DTOs
 * (inspection list/detail, questions, summary). The editable answer detail goes
 * through repo.getAnswerDetail (cache + outbox overlay) instead.
 */
import type { Repo } from "./repo";
import { ApiError } from "@/lib/api-client";
import { isReachable, markReachable, ensureFreshReachability, isOfflineEnabled } from "./connectivity";

/** A read that stalls this long is treated as a network failure (offline). The
 *  dangling request is abandoned — harmless for a read (no side effects). */
const READ_TIMEOUT_MS = 8000;
class TimeoutError extends Error { constructor() { super("read timed out"); this.name = "TimeoutError"; } }
function withTimeout<T>(p: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new TimeoutError()), READ_TIMEOUT_MS);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

async function fromCache<T>(
  repo: Repo,
  key: string,
  offlineFallback?: () => Promise<T | undefined>,
): Promise<T | undefined> {
  const cached = await repo.kvGet<T>(key);
  if (cached !== undefined) return cached;
  if (offlineFallback) {
    const assembled = await offlineFallback();
    if (assembled !== undefined) return assembled;
  }
  return undefined;
}

export async function readThrough<T>(
  repo: Repo,
  key: string,
  onlineFetch: () => Promise<T>,
  offlineFallback?: () => Promise<T | undefined>,
): Promise<T> {
  // Online-only roles (admin / super_admin) never touch the local cache: go
  // straight to the network so they always see live data and an error
  // (404/403) surfaces instead of a stale mirrored copy.
  if (!isOfflineEnabled()) return onlineFetch();
  // Confirm reachability (TTL-cached) BEFORE deciding, so an offline transition
  // is detected and we serve Dexie directly instead of firing a doomed request.
  await ensureFreshReachability();
  // Fast path: known-unreachable → serve cache, skip the request entirely.
  if (!isReachable()) {
    const cached = await fromCache(repo, key, offlineFallback);
    if (cached !== undefined) return cached;
  }
  // Attempt the network; fall back to cache on ANY failure — including a STALL.
  // A dead backend behind the dev/preview proxy (or nginx) can hold a request
  // open instead of failing fast; without a timeout the queryFn would hang
  // forever, the screen would never fall back to the cache, and reachability
  // would never flip — which is what made offline screens (and the rectification
  // form's "missing root cause" gate) appear frozen. Race a timeout so a stall
  // is treated as a network failure.
  try {
    const data = await withTimeout(onlineFetch());
    markReachable(true);            // request succeeded → we're online
    await repo.kvPut(key, data);
    return data;
  } catch (err) {
    // An HTTP error response means the SERVER IS REACHABLE — it just rejected
    // this request. That's not an offline condition, so don't flip to offline.
    // 4xx are authoritative (404 = deleted, 403 = access lost, 401 = expired):
    // propagate them so the UI reacts (e.g. shows "not found") instead of
    // serving a STALE cached copy of a row the server says is gone. Only 5xx
    // and genuine network failures fall back to the cache as a safety net.
    if (err instanceof ApiError) {
      markReachable(true);
      if (err.status >= 400 && err.status < 500) throw err;
    } else {
      markReachable(false);         // network failure → flip to offline so the
                                    // next reads skip straight to cache
    }
    const cached = await fromCache(repo, key, offlineFallback);
    if (cached !== undefined) return cached;
    throw err instanceof Error ? err : new Error("This data isn't available offline yet — connect once to cache it.");
  }
}
